import { useContext, useState, useEffect } from 'react'
import { useHistory } from 'react-router-dom'

import { useLocation } from '../../Location/Location.hook'
import { QuizQuestionContext, SCREEN_QUIZ_QUESTION } from '../../../contextAPI'
import { PATH_URL } from 'src/helpers'
import { QuizType } from 'src/pages/shared'
import { QuizResponse } from 'src/types'

export const quizTakerRolesNew = [
  { label: "I'm helping someone getting married", value: 'FRIEND_FAMILY' },
  { label: "I'm getting married", value: 'BRIDE_GROOM' },
  { label: "I'm a wedding planner", value: 'PLANNER' },
  { label: 'Just browsing', value: 'BROWSING' },
]

export const plannerStatusesNew = [
  { label: 'Hired by client', value: 'HIRED' },
  { label: 'Exploring options for client', value: 'EXPLORING' },
]

const upsertQuizResponse = (responses: QuizResponse[], key: string, values: string): QuizResponse[] => {
  const idx = responses.findIndex((e) => e.key === key)
  if (idx > -1) {
    return responses.map((e) => (e.key === key ? { ...e, values } : e))
  }
  return [...responses, { key, values }]
}

export const useNewPlanningStage = (quizType?: QuizType) => {
  const history = useHistory()
  const { data, setQuizScreen, weddingDateData } = useContext(QuizQuestionContext)
  const [stage, setStage] = useState<string>(data.planningStage?.stage ?? '')
  const [role, setRole] = useState<string>(data.planningStage?.role ?? '')
  const [plannerStatus, setPlannerStatus] = useState<string>(data.planningStage?.plannerStatus ?? '')
  const location = useLocation()

  const navigateNext = () => {
    if (quizType === QuizType.PINTEREST) {
      history.push({
        pathname: PATH_URL.GUEST_COUNT,
        search: new URLSearchParams({ quizType }).toString(),
      })
    } else {
      history.push({ pathname: PATH_URL.GUEST_COUNT })
    }
    setQuizScreen(SCREEN_QUIZ_QUESTION.GUEST_COUNT)
  }

  const persistAndMaybeAdvance = (nextStage: string, nextRole: string, nextPlannerStatus: string) => {
    const shouldAdvance = nextStage !== '' && nextRole !== '' && (nextRole !== 'PLANNER' || nextPlannerStatus !== '')
    const currentTempPoppyData = JSON.parse(localStorage.getItem('tempPoppySessionData') || '{}')
    let responses: QuizResponse[] = currentTempPoppyData.quizResponses || []

    responses = upsertQuizResponse(responses, 'PLANNING_STAGE', nextStage)
    if (nextRole) {
      responses = upsertQuizResponse(responses, 'QUIZ_TAKER_ROLE', nextRole)
    }
    if (nextRole === 'PLANNER' && nextPlannerStatus) {
      responses = upsertQuizResponse(responses, 'PLANNER_STATUS', nextPlannerStatus)
    } else {
      responses = responses.filter((e) => e.key !== 'PLANNER_STATUS')
    }

    localStorage.setItem('tempPoppySessionData', JSON.stringify({ ...currentTempPoppyData, quizResponses: responses }))

    if (shouldAdvance) {
      setTimeout(() => {
        navigateNext()
      }, 600)
    }
  }

  const handleBack = async () => {
    if (window.history.length > 1) {
      history.goBack()
    } else {
      if (quizType === QuizType.PINTEREST) {
        history.push({
          pathname: PATH_URL.NEW_COLOR_PALETTE_PAGE,
          search: new URLSearchParams({ quizType }).toString(),
        })
        setQuizScreen(SCREEN_QUIZ_QUESTION.NEW_COLOR_PALETTE_PAGE)
      } else {
        history.push({ pathname: PATH_URL.YOUR_DETAILS })
        setQuizScreen(SCREEN_QUIZ_QUESTION.YOUR_DETAILS)
      }
    }
  }

  const handleChangeStage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target
    setStage(value)
    persistAndMaybeAdvance(value, role, plannerStatus)
  }

  const handleClickStage = (value: string) => {
    if (value === stage) {
      persistAndMaybeAdvance(value, role, plannerStatus)
    }
  }

  const handleChangeRole = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target
    const nextPlannerStatus = value !== 'PLANNER' ? '' : plannerStatus
    setRole(value)
    setPlannerStatus(nextPlannerStatus)
    persistAndMaybeAdvance(stage, value, nextPlannerStatus)
  }

  const handleChangePlannerStatus = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target
    setPlannerStatus(value)
    persistAndMaybeAdvance(stage, role, value)
  }

  useEffect(() => {
    const dataFromLocal = JSON.parse(localStorage.getItem('tempPoppySessionData') || '{}')
    const quizResponsesFromLocal: QuizResponse[] = dataFromLocal?.quizResponses || []
    const stageFromLocal = quizResponsesFromLocal.find((e) => e.key === 'PLANNING_STAGE')
    const roleFromLocal = quizResponsesFromLocal.find((e) => e.key === 'QUIZ_TAKER_ROLE')
    const plannerStatusFromLocal = quizResponsesFromLocal.find((e) => e.key === 'PLANNER_STATUS')
    setStage(data.planningStage?.stage || (stageFromLocal?.values as string) || '')
    setRole(data.planningStage?.role || (roleFromLocal?.values as string) || '')
    setPlannerStatus(data.planningStage?.plannerStatus || (plannerStatusFromLocal?.values as string) || '')
  }, [data.planningStage?.stage, data.planningStage?.role, data.planningStage?.plannerStatus])

  return {
    data,
    location,
    weddingDateData,
    handleBack,
    stage,
    role,
    plannerStatus,
    handleChangeStage,
    handleClickStage,
    handleChangeRole,
    handleChangePlannerStatus,
  }
}
